/**
 * @fileoverview Search Page Module.
 * This module sets up and initializes the Algolia InstantSearch for the general search page.
 * It handles multiple indices such as actions, global networks, resources, events, and news.
 */
import { elementExists, getContentTypeFromUrl } from "../Utils";
import instantsearch from "instantsearch.js";
import { configure, index, searchBox } from "instantsearch.js/es/widgets";
import algoliasearch from "algoliasearch";
import { connectHits } from "instantsearch.js/es/connectors";
import SearcherHits from "./widgets/SearcherHits";
import customPaginationMultiIndex from "./widgets/CustomPaginationMultiIndex";
import {
    getNoResultsHtml,
    setMostRelevantResultsCounter,
    updateResultCounter
} from "./Utils";
import {
    getIndicesWithKeysAsObject,
    roundRobinBestResultsFromAllIndices
} from "../Components/SearchPage/SearchPageUtils";
import { Indices } from "../Components/SearchPage/enums/indices";
import {
    handleChangeTab,
    hideMostRelevantResultsHandler,
    setTabResultsCounter,
    showMostRelevantResultsHandler
} from "../Components/SearchPage/navigation/tabManager";
import { hideOrShowPagination } from "../Components/SearchPage/navigation/paginationUtils";

const newsContainerSelector = '#search-page-js';
const searchboxSelector = '#searchbox';

/**
 * @constant
 * @type {object}
 * @description Algolia search client instance configured with application ID and search-only API key.
 */
const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);


const indices = getIndicesWithKeysAsObject();
const actionsIndex = indices[Indices.action];

const most_relevant_selector = '#most_relevant_results_hits';
const actions_hits_selector = '#actions_hits';
const global_networks_hits_selector = '#global_network_hits';
const resources_hits_selector = '#resources_hits';
const events_hits_selector = '#events_hits';
const news_hits_selector = '#news_hits';
const contacts_hits_selector = '#contacts_hits';

const mostRelevantResultsSlug = 'most_relevant_results';
const paginationSelector = '#pagination';
const hitsPerPage = 10;

/**
 * @constant
 * @type {string}
 * @description Current query extracted from the URL parameters.
 */
const searchSubmitTemplate = `
<div class="absolute left-3 top-6 search-icon-js">
    <svg class="h-[18.28px]" width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.877 13.7927L17.612 18.5798" stroke="black"/>
    <path d="M13.73 7.13414C13.73 10.6401 10.9202 13.4729 7.46557 13.4729C4.01097 13.4729 1.20117 10.6401 1.20117 7.13414C1.20117 3.62819 4.01097 0.79541 7.46557 0.79541C10.9202 0.79541 13.73 3.62819 13.73 7.13414Z" stroke="#2A2824"/>
    </svg>
</div>
`;

/**
 * Initializes the search page module.
 * Checks if the container exists and initializes the searcher if it does.
 */
const init = () => {
    if (!elementExists(newsContainerSelector)) return;
    setupSearcher();
    initSearcher();
}

/**
 * Sets up the searcher by determining the initial tab based on query and content type.
 */
const setupSearcher = () => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('query');
    const currentType = getContentTypeFromUrl();

    const initialTab = determineInitialTab(query, currentType);
    handleChangeTab(initialTab);
}

/**
 * Determines the initial tab based on the query and content type.
 * @param {string} query - The search query.
 * @param {string} contentType - The current content type.
 * @returns {string} The initial tab.
 */
const determineInitialTab = (query, contentType) => {
    if (contentType === 'most_relevant_results' && !query) {
        return 'actions';
    } else if (!contentType) {
        return query ? 'most_relevant_results' : 'actions';
    } else {
        return contentType;
    }
}

/**
 * Initializes and configures the Algolia InstantSearch for the search page.
 * Sets up search parameters, custom widgets for multiple indices, and starts the search process.
 */
const initSearcher = () => {
    const search = instantsearch({
        indexName: actionsIndex,
        searchClient,
        routing: {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[actionsIndex];
                    return {
                        query: indexUiState.query,
                        content_type: getContentTypeFromUrl(),
                        pg: indexUiState.page
                    }
                },
                routeToState(routeState) {
                    return {
                        [actionsIndex]: {
                            query: routeState.query,
                            content_type: routeState.content_type,
                            page: routeState.pg || 1
                        },
                    };
                },
            },
        },
    });

    /**
     * Sets event listeners for custom search events.
     */
    const setListeners = () => {
        document.addEventListener('filterClicked', resetPagination);
        document.addEventListener('searchExecuted', searchExecutedHandler);
    }

    /**
     * Resets the pagination and fetches interleaved results based on the provided slug.
     * @param {Event} ev - The event triggered when a filter is clicked.
     */
    const resetPagination = (ev) => {
        const searchState = search.helper.state.setPage(0);
        search.helper.setState(searchState).search();
        if (ev.detail.slug === mostRelevantResultsSlug) {
            fetchAndRenderInterleavedResults(ev.detail.slug).catch((error) => {
                console.error('An error occurred:', error);
            });
        }
    }

    /**
     * Activates loading state for the given slug.
     * @param {string} slug - The slug for which to activate loading.
     */
    const activateLoading = (slug) => {
        const tabSelectorId = `${slug}_tab`;
        const el = document.getElementById(tabSelectorId);
        el.textContent = 'Loading...';
    }

    /**
     * Handles the search execution event.
     * @param {Event} ev - The event triggered when a search is executed.
     */
    const searchExecutedHandler = (ev) => {
        const query = ev.detail.query;
        executeSearch(query);
        setTabAfterSearch(query);

        if (query) {
            fetchAndRenderInterleavedResults().catch((error) => {
                console.error('An error occurred:', error);
            })
        }
    }

    /**
     * Executes a search with the given query.
     * @param {string} query - The search query.
     */
    const executeSearch = (query) => {
        search.helper.setQuery(query).search();
    }

    /**
     * Sets the tab after a search is executed.
     * @param {string} query - The search query.
     */
    const setTabAfterSearch = (query) => {
        if (query) {
            handleChangeTab(mostRelevantResultsSlug);
            showMostRelevantResultsHandler();
        } else {
            handleChangeTab('actions');
            hideMostRelevantResultsHandler();
        }
    }

    /**
     * Custom hits connector for displaying search results.
     * @param {object} hits - The search hits.
     */
    const customHits = connectHits((hits) => {
        const { type } = hits.widgetParams;
        SearcherHits(hits, type);
        handlePostRenderActions(hits, type);
    });

    /**
     * Handles post-render actions such as updating counters and pagination.
     * @param {object} hits - The search hits.
     * @param {string} type - The type of hits.
     */
    const handlePostRenderActions = (hits, type) => {
        const currentType = getContentTypeFromUrl()
        setTabResultsCounter(type, hits.results?.nbHits);
        if (currentType === type) {
            window.results = hits.results;
            hideOrShowPagination(type, window.results?.nbHits);
            updateResultCounter(hits);
        }
    }

    /**
     * Gets the current query from the search box or URL parameters.
     * @returns {string} The current query.
     */
    const getQuery = () => {
        const inputValue = document.querySelector(searchboxSelector + ' input')?.value;
        const params = new URLSearchParams(window.location.search);
        return inputValue ?? params.get('query') ?? '';
    }

    /**
     * Fetches and renders interleaved results for all indices.
     * @param {string} [slug=null] - The slug for which to fetch results.
     */
    const fetchAndRenderInterleavedResults = async (slug = null) => {
        try {
            const query = getQuery();
            const hitsToRender = await roundRobinBestResultsFromAllIndices(searchClient, query, hitsPerPage);
            setTabResultsCounter(mostRelevantResultsSlug, hitsToRender?.length);

            const contentType = slug ?? getContentTypeFromUrl();
            if (contentType && contentType !== mostRelevantResultsSlug) return;

            const allHitsContainer = document.querySelector(most_relevant_selector);
            setMostRelevantResultsCounter(hitsToRender?.length);

            if (hitsToRender?.length) SearcherHits({ hits: hitsToRender, widgetParams: { container: allHitsContainer } }, 'all');
            else allHitsContainer.innerHTML = getNoResultsHtml();
        } catch (error) {
            console.error('Error fetching interleaved results: ', error);
        }
    };

    fetchAndRenderInterleavedResults().catch((error) => {
        console.error('An error occurred:', error);
    });

    search.addWidgets([
        configure({
            hitsPerPage,
        }),
        searchBox({
            container: searchboxSelector,
            placeholder: 'Search',
            showSubmit: true,
            queryHook(query, search) { },
            templates: {
                submit: searchSubmitTemplate
            }
        }),
        customHits({
            container: document.querySelector(actions_hits_selector),
            type: 'actions',
        }),
        index({ indexName: indices[Indices.globalNetwork] }).addWidgets([
            customHits({
                container: document.querySelector(global_networks_hits_selector),
                type: 'global_network'
            }),
        ]),
        index({ indexName: indices[Indices.resource] }).addWidgets([
            customHits({
                container: document.querySelector(resources_hits_selector),
                type: 'resources'
            }),
        ]),
        index({ indexName: indices[Indices.event] }).addWidgets([
            customHits({
                container: document.querySelector(events_hits_selector),
                type: 'events'
            }),
        ]),
        index({ indexName: indices[Indices.news] }).addWidgets([
            customHits({
                container: document.querySelector(news_hits_selector),
                type: 'news'
            }),
        ]),
        index({ indexName: indices[Indices.contacts] }).addWidgets([
            customHits({
                container: document.querySelector(contacts_hits_selector),
                type: 'contacts'
            }),
        ]),
        customPaginationMultiIndex({
            container: document.querySelector(paginationSelector),
            topContainer: 'topContainer'
        }),
    ]);

    search.start();
    setListeners();
};

init();
